// The backend listens on 8085 (backend/src/main/resources/application.properties). This was
// 8084 — inherited verbatim from the parent project, where that was correct — which meant the
// carved client could not reach its own backend at all. The client's build gate is a compile,
// so nothing caught it.
//
// Phase 14 (deploy kit): this used to be the hardcoded 'http://localhost:8085/api' above, with
// the production URL commented out on the next line for someone to remember to swap by hand. A
// staging build therefore shipped localhost and could not reach its own API. It is now
// environment-driven the CRA way.
//
// The default is the RELATIVE, same-origin '/api', which is exactly what deploy/nginx-pos-staging.conf
// serves (spec section 2: one host, path split -- '/' back office, '/counter/' till, '/api/' ->
// 127.0.0.1:8085). So a build with no env var set is already correct for a real deployment; the
// override exists for anyone serving the client from a different origin than the API.
//
// Local development: `npm start` runs on :3000 while the backend runs on :8085, so a relative
// '/api' would hit the dev server. package.json's "proxy" field forwards it to :8085, which keeps
// the same-origin default honest in dev too. Alternatively put
// REACT_APP_API_BASE_URL=http://localhost:8085/api in client/.env.local.
const BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export const ENDPOINTS = {
    AUTH: {
        LOGIN: `${BASE_URL}/authentication`,
        REFRESH: `${BASE_URL}/auth/refresh-token`,
        IMPERSONATE: (userId: string) => `${BASE_URL}/authentication/impersonate/${userId}`,
        VERIFY_TWO_FACTOR: `${BASE_URL}/authentication/verify`,
    },
    IMPERSONATION_GRANT: {
        MY: `${BASE_URL}/impersonation-grant/my`,
        AVAILABLE: `${BASE_URL}/impersonation-grant/available`,
        BASE: `${BASE_URL}/impersonation-grant`,
    },
    USER_PROFILE: {
        LOGGED_IN_USER_PROFILE: `${BASE_URL}/user-profile/logged-in-user-profile`,
        UPDATE_PROFILE: (id: string) => `${BASE_URL}/user-profile/${id}`,
        BY_USER: (userId: string) => `${BASE_URL}/user-profile/user/${userId}`,
    },
    TWO_FACTOR: {
        SETUP: `${BASE_URL}/user/2fa/setup`,
        ENABLE: `${BASE_URL}/user/2fa/enable`,
        DISABLE: `${BASE_URL}/user/2fa/disable`,
        REGENERATE_BACKUP_CODES: `${BASE_URL}/user/2fa/backup-codes/regenerate`,
        STATUS: `${BASE_URL}/user/2fa/status`,
        ADMIN_RESET: (userId: string) => `${BASE_URL}/user/2fa/admin/reset/${userId}`,
        ADMIN_BLOCK_BACKUP_CODES: (userId: string) => `${BASE_URL}/user/2fa/admin/backup-codes/block/${userId}`,
        ADMIN_UNBLOCK_BACKUP_CODES: (userId: string) => `${BASE_URL}/user/2fa/admin/backup-codes/unblock/${userId}`,
        ADMIN_STATUS: (userId: string) => `${BASE_URL}/user/2fa/admin/status/${userId}`,
    },
    USER: {
        BASE: `${BASE_URL}/user`,
        INSTITUTION_USERS: `${BASE_URL}/user/institution-users`,
        UPDATE_TYPE: `${BASE_URL}/user/update-type`,
        UPDATE_PASSWORD: `${BASE_URL}/user/update-password`,
        RESET_PASSWORD: (userId: string, institutionId: string) => `${BASE_URL}/user/${userId}/institution/${institutionId}/reset-user-password`,
        UPDATE_USERNAME: (userId: string) => `${BASE_URL}/user/update-username/${userId}`,
        CHECK_USERNAME: `${BASE_URL}/user/check-username-available`,
        TOGGLE_ACTIVE: (id: string) => `${BASE_URL}/user/toggle-active/${id}`,
        DELETE: (id: string) => `${BASE_URL}/user/${id}`,
        PERMISSIONS: (userId: string, institutionId: string) =>
            `${BASE_URL}/user/${userId}/permissions/${institutionId}`,
    },
    INSTITUTION: {
        BASE: `${BASE_URL}/institution`,
        LOGGED_IN_USER_INSTITUTIONS: `${BASE_URL}/institution/logged-in-user-institutions`,
        CHECK_CODE_UNIQUENESS: `${BASE_URL}/institution/check-code-unique`,
        UPDATE: (id: string) => `${BASE_URL}/institution/${id}`,
        DELETE: (id: string) => `${BASE_URL}/institution/${id}`,
        TOGGLE_ACTIVE: (id: string) => `${BASE_URL}/institution/toggle-active/${id}`,
        BY_USER_ID: (userId: string) => `${BASE_URL}/institution/user/${userId}`,
        PREMIUM_DASHBOARD: (id: string) => `${BASE_URL}/institution/${id}/premium-dashboard`,
        ENABLED_MODULES: (id: string) => `${BASE_URL}/institution/${id}/enabled-modules`,
    },
    INSTITUTION_SETTINGS: {
        BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/institution-settings/institution/${institutionId}`,
        TWO_FACTOR_POLICY: (institutionId: string) => `${BASE_URL}/institution-settings/institution/${institutionId}/two-factor-policy`,
    },
    STORE: {
        BASE: `${BASE_URL}/store`,
        LIST_BY_INSTITUTION: `${BASE_URL}/store/institution`,
        DELETE: (id: string) => `${BASE_URL}/store/${id}`
    },
    ITEM_CATEGORY: {
        BASE: `${BASE_URL}/item-category`,
        LIST_BY_INSTITUTION: `${BASE_URL}/item-category/institution`,
        UPDATE: (id: string) => `${BASE_URL}/item-category/${id}`,
        DELETE: (id: string) => `${BASE_URL}/item-category/${id}`
    },
    ITEM: {
        BASE: `${BASE_URL}/item`,
        LIST_BY_INSTITUTION: `${BASE_URL}/item/institution`,
        UPDATE: (id: string) => `${BASE_URL}/item/${id}`,
        DELETE: (id: string) => `${BASE_URL}/item/${id}`
    },
    PUBLIC: {
        CHECK_CODE_UNIQUENESS: `${BASE_URL}/public/institution/check-code-unique`,
        INSTITUTION_CREATE: `${BASE_URL}/public/institution`
    },
    STOCK: {
        STOCK_BY_STORE_AND_ITEM: (storeId: string, itemId: string) => `${BASE_URL}/stock/store-id/${storeId}/item-id/${itemId}`,
        STOCK_IN: `${BASE_URL}/stock/stock-in`,
        STOCK_OUT: `${BASE_URL}/stock/stock-out`,
        STOCK_BY_STORE: (storeId: string) => `${BASE_URL}/stock/store-id/${storeId}`,
        STOCK_HISTORY: (storeId: string, itemId: string) => `${BASE_URL}/stock/history/store-id/${storeId}/item-id/${itemId}`,
        LOTS: (storeId: string, itemId: string) => `${BASE_URL}/stock/lots/${storeId}/${itemId}`,
        REORDER_LEVEL: (stockId: string) => `${BASE_URL}/stock/${stockId}/reorder-level`,
        LOW_STOCK: (institutionId: string) => `${BASE_URL}/stock/institution/${institutionId}/low-stock`,
    },
    STOCKTAKE: {
        BASE: `${BASE_URL}/stocktake`,
        CREATE: `${BASE_URL}/stocktake`,
        GET_BY_ID: (id: string) => `${BASE_URL}/stocktake/${id}`,
        COUNTS: (id: string) => `${BASE_URL}/stocktake/${id}/counts`,
        APPROVE: (id: string) => `${BASE_URL}/stocktake/${id}/approve`,
        CANCEL: (id: string) => `${BASE_URL}/stocktake/${id}/cancel`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/stocktake/institution/${institutionId}`,
    },
    STOCK_TRANSFER: {
        BASE: `${BASE_URL}/stock-transfer`,
        CREATE: `${BASE_URL}/stock-transfer`,
        GET_BY_ID: (id: string) => `${BASE_URL}/stock-transfer/${id}`,
        SUBMIT: (id: string) => `${BASE_URL}/stock-transfer/${id}/submit`,
        APPROVE: (id: string) => `${BASE_URL}/stock-transfer/${id}/approve`,
        CANCEL: (id: string) => `${BASE_URL}/stock-transfer/${id}/cancel`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/stock-transfer/institution/${institutionId}`,
    },
    ACCOUNT: {
        BASE: `${BASE_URL}/account`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/account/institution/${institutionId}`,
        TREE_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/account/institution/${institutionId}/tree`,
        BY_TYPE: (institutionId: string, type: string) => `${BASE_URL}/account/institution/${institutionId}/type/${type}`,
        BY_ID: (id: string) => `${BASE_URL}/account/${id}`,
        UPDATE: (id: string) => `${BASE_URL}/account/${id}`,
        DELETE: (id: string) => `${BASE_URL}/account/${id}`,
        HIDE: (institutionId: string, accountId: string) => `${BASE_URL}/account/institution/${institutionId}/hide/${accountId}`,
        UNHIDE: (institutionId: string, accountId: string) => `${BASE_URL}/account/institution/${institutionId}/hide/${accountId}`,
        HIDDEN: (institutionId: string) => `${BASE_URL}/account/institution/${institutionId}/hidden`,
        LEDGER: (id: string) => `${BASE_URL}/account/${id}/ledger`,
        TRIAL_BALANCE: (institutionId: string) => `${BASE_URL}/account/institution/${institutionId}/trial-balance`,
        SEGMENT_TRIAL_BALANCE: (institutionId: string) => `${BASE_URL}/account/institution/${institutionId}/segment-trial-balance`,
        INCOME_STATEMENT: (institutionId: string) => `${BASE_URL}/account/institution/${institutionId}/income-statement`,
        BALANCE_SHEET: (institutionId: string) => `${BASE_URL}/account/institution/${institutionId}/balance-sheet`,
    },
    SEGMENT: {
        BASE: `${BASE_URL}/segment`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/segment/institution/${institutionId}`,
        BY_ID: (id: string) => `${BASE_URL}/segment/${id}`,
        UPDATE: (id: string) => `${BASE_URL}/segment/${id}`,
    },
    SEGMENT_SETTINGS: {
        BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/segment-settings/institution/${institutionId}`,
        UPDATE: (institutionId: string) => `${BASE_URL}/segment-settings/institution/${institutionId}`,
    },
    LEDGER_CLOSE_SETTINGS: {
        BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/ledger-close-settings/institution/${institutionId}`,
        UPDATE: (institutionId: string) => `${BASE_URL}/ledger-close-settings/institution/${institutionId}`,
    },
    JOURNAL_ENTRY: {
        BASE: `${BASE_URL}/journal-entry`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/journal-entry/institution/${institutionId}`,
        BY_ID: (id: string) => `${BASE_URL}/journal-entry/${id}`,
        POST: (id: string) => `${BASE_URL}/journal-entry/${id}/post`,
        VOID: (id: string) => `${BASE_URL}/journal-entry/${id}/void`,
    },
    TRANSACTION: {
        BASE: `${BASE_URL}/transaction`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/transaction/institution/${institutionId}`,
        BY_ID: (id: string) => `${BASE_URL}/transaction/${id}`,
        DELETE: (id: string) => `${BASE_URL}/transaction/${id}`,
    },
    DASHBOARD: {
        STORE_ADMIN: (institutionId: string) => `${BASE_URL}/dashboard/store-admin/institution/${institutionId}`,
        ACCOUNTING_SUMMARY: (institutionId: string) =>
            `${BASE_URL}/dashboard/accounting/summary/institution/${institutionId}`,
        INVENTORY_SUMMARY: (institutionId: string) =>
            `${BASE_URL}/dashboard/inventory/summary/institution/${institutionId}`,
        SALES_SUMMARY: (institutionId: string) =>
            `${BASE_URL}/dashboard/sales/summary/institution/${institutionId}`,
        LAYOUT: (institutionId: string, boardKey = 'home') =>
            `${BASE_URL}/dashboard/layout/institution/${institutionId}?boardKey=${encodeURIComponent(boardKey)}`,
        KPI_TARGETS: (institutionId: string) =>
            `${BASE_URL}/dashboard/kpi-targets/institution/${institutionId}`,
        ACCOUNTING_AR_AGING: (institutionId: string) =>
            `${BASE_URL}/dashboard/accounting/ar-aging/institution/${institutionId}`,
        ACCOUNTING_TOP_RECEIVABLES: (institutionId: string) =>
            `${BASE_URL}/dashboard/accounting/top-receivables/institution/${institutionId}`,
        ACCOUNTING_TREND: (institutionId: string) =>
            `${BASE_URL}/dashboard/accounting/trend/institution/${institutionId}`,
        ACCOUNTING_EXPENSE_BY_ACCOUNT: (institutionId: string) =>
            `${BASE_URL}/dashboard/accounting/expense-by-account/institution/${institutionId}`,
        INVENTORY_VALUE_BY_STORE: (institutionId: string) =>
            `${BASE_URL}/dashboard/inventory/value-by-store/institution/${institutionId}`,
        INVENTORY_MOVEMENT: (institutionId: string) =>
            `${BASE_URL}/dashboard/inventory/movement/institution/${institutionId}`,
        INVENTORY_REORDER: (institutionId: string) =>
            `${BASE_URL}/dashboard/inventory/reorder/institution/${institutionId}`,
        INVENTORY_TOP_ITEMS: (institutionId: string) =>
            `${BASE_URL}/dashboard/inventory/top-items/institution/${institutionId}`,
        SALES_PIPELINE: (institutionId: string) =>
            `${BASE_URL}/dashboard/sales/pipeline/institution/${institutionId}`,
        SALES_REVENUE: (institutionId: string) =>
            `${BASE_URL}/dashboard/sales/revenue/institution/${institutionId}`,
        SALES_OUTCOMES: (institutionId: string) =>
            `${BASE_URL}/dashboard/sales/outcomes/institution/${institutionId}`,
        SALES_TOP_DEALS: (institutionId: string) =>
            `${BASE_URL}/dashboard/sales/top-deals/institution/${institutionId}`,
    },
    UNIT_OF_MEASUREMENT: {
        ALL: `${BASE_URL}/unit-of-measurement`,
        BY_ID: (id: string) => `${BASE_URL}/unit-of-measurement/${id}`,
    },
    SUPPLIER: {
        BASE: `${BASE_URL}/supplier`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/supplier/institution/${institutionId}`,
        GET_BY_ID: (id: string) => `${BASE_URL}/supplier/${id}`,
        CREATE: `${BASE_URL}/supplier`,
        UPDATE: (id: string) => `${BASE_URL}/supplier/${id}`,
        DELETE: (id: string) => `${BASE_URL}/supplier/${id}`,
    },
    SUPPLIER_ITEM: {
        BASE: `${BASE_URL}/supplier-item`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/supplier-item/institution/${institutionId}`,
        LIST_BY_SUPPLIER: (supplierId: string) => `${BASE_URL}/supplier-item/supplier/${supplierId}`,
        BY_ITEM: (itemId: string) => `${BASE_URL}/supplier-item/by-item/${itemId}`,
        GET_BY_ID: (id: string) => `${BASE_URL}/supplier-item/${id}`,
        CREATE: `${BASE_URL}/supplier-item`,
        UPDATE: (id: string) => `${BASE_URL}/supplier-item/${id}`,
        DELETE: (id: string) => `${BASE_URL}/supplier-item/${id}`,
    },
    PROCUREMENT_SETTINGS: {
        GET_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/procurement-settings/institution/${institutionId}`,
        UPSERT: (institutionId: string) => `${BASE_URL}/procurement-settings/institution/${institutionId}`,
    },
    PURCHASE_ORDER: {
        BASE: `${BASE_URL}/purchase-order`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/purchase-order/institution/${institutionId}`,
        GET_BY_ID: (id: string) => `${BASE_URL}/purchase-order/${id}`,
        PDF: (id: string) => `${BASE_URL}/purchase-order/${id}/pdf`,
        CREATE: `${BASE_URL}/purchase-order`,
        UPDATE_DRAFT: (id: string) => `${BASE_URL}/purchase-order/${id}`,
        SUBMIT: (id: string) => `${BASE_URL}/purchase-order/${id}/submit`,
        APPROVE: (id: string) => `${BASE_URL}/purchase-order/${id}/approve`,
        REJECT: (id: string) => `${BASE_URL}/purchase-order/${id}/reject`,
        MARK_ORDERED: (id: string) => `${BASE_URL}/purchase-order/${id}/mark-ordered`,
        CANCEL: (id: string) => `${BASE_URL}/purchase-order/${id}/cancel`,
        BULK_APPROVE: `${BASE_URL}/purchase-order/bulk-approve`,
        BULK_REJECT: `${BASE_URL}/purchase-order/bulk-reject`,
        MINOR_AMENDMENT: (id: string) => `${BASE_URL}/purchase-order/${id}/minor-amendment`,
        AMEND: (id: string) => `${BASE_URL}/purchase-order/${id}/amend`,
        DELETE: (id: string) => `${BASE_URL}/purchase-order/${id}`,
    },
    GOODS_RECEIPT: {
        BASE: `${BASE_URL}/goods-receipt`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/goods-receipt/institution/${institutionId}`,
        GET_BY_ID: (id: string) => `${BASE_URL}/goods-receipt/${id}`,
        CREATE: `${BASE_URL}/goods-receipt`,
        POST: (id: string) => `${BASE_URL}/goods-receipt/${id}/post`,
        REVERSE: (id: string) => `${BASE_URL}/goods-receipt/${id}/reverse`,
    },
    SUPPLIER_BILL: {
        BASE: `${BASE_URL}/supplier-bill`,
        LIST_BY_PURCHASE_ORDER: (purchaseOrderId: string) => `${BASE_URL}/supplier-bill/purchase-order/${purchaseOrderId}`,
        LIST_BY_SUPPLIER: (supplierId: string) => `${BASE_URL}/supplier-bill/supplier/${supplierId}`,
        GET_BY_ID: (id: string) => `${BASE_URL}/supplier-bill/${id}`,
        CREATE: `${BASE_URL}/supplier-bill`,
        REVERSE: (id: string) => `${BASE_URL}/supplier-bill/${id}/reverse`,
        RELEASE_BLOCK: (id: string) => `${BASE_URL}/supplier-bill/${id}/release-block`,
        EDIT: (id: string) => `${BASE_URL}/supplier-bill/${id}/edit`,
        EDIT_HISTORY: (id: string) => `${BASE_URL}/supplier-bill/${id}/edit-history`,
    },
    SUPPLIER_PAYMENT: {
        BASE: `${BASE_URL}/supplier-payment`,
        LIST_BY_BILL: (supplierBillId: string) => `${BASE_URL}/supplier-payment/bill/${supplierBillId}`,
        GET_BY_ID: (id: string) => `${BASE_URL}/supplier-payment/${id}`,
        CREATE: `${BASE_URL}/supplier-payment`,
        REVERSE: (id: string) => `${BASE_URL}/supplier-payment/${id}/reverse`,
        EDIT: (id: string) => `${BASE_URL}/supplier-payment/${id}/edit`,
        EDIT_HISTORY: (id: string) => `${BASE_URL}/supplier-payment/${id}/edit-history`,
    },
    CUSTOMER: {
        BASE: `${BASE_URL}/customer`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/customer/institution/${institutionId}`,
        GET_BY_ID: (id: string) => `${BASE_URL}/customer/${id}`,
        CREATE: `${BASE_URL}/customer`,
        UPDATE: (id: string) => `${BASE_URL}/customer/${id}`,
        DELETE: (id: string) => `${BASE_URL}/customer/${id}`,
    },
    SALES_SETTINGS: {
        BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/sales-settings/institution/${institutionId}`,
        UPSERT: `${BASE_URL}/sales-settings`,
    },
    INVOICE_BRANDING: {
        BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/invoice-branding/institution/${institutionId}`,
        UPSERT: `${BASE_URL}/invoice-branding`,
        LOGO: (institutionId: string) => `${BASE_URL}/invoice-branding/institution/${institutionId}/logo`,
    },
    SALES_INVOICE: {
        BASE: `${BASE_URL}/sales-invoice`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/sales-invoice/institution/${institutionId}`,
        LIST_BY_CUSTOMER: (customerId: string) => `${BASE_URL}/sales-invoice/customer/${customerId}`,
        GET_BY_ID: (id: string) => `${BASE_URL}/sales-invoice/${id}`,
        CREATE: `${BASE_URL}/sales-invoice`,
        UPDATE: (id: string) => `${BASE_URL}/sales-invoice/${id}`,
        DELETE: (id: string) => `${BASE_URL}/sales-invoice/${id}`,
        ISSUE: (id: string) => `${BASE_URL}/sales-invoice/${id}/issue`,
        VOID: (id: string) => `${BASE_URL}/sales-invoice/${id}/void`,
        EDIT: (id: string) => `${BASE_URL}/sales-invoice/${id}/edit`,
        EDIT_HISTORY: (id: string) => `${BASE_URL}/sales-invoice/${id}/edit-history`,
        PDF: (id: string) => `${BASE_URL}/sales-invoice/${id}/pdf`,
        EMAIL: (id: string) => `${BASE_URL}/sales-invoice/${id}/email`,
    },
    RECEIPT: {
        LIST_BY_INVOICE: (invoiceId: string) => `${BASE_URL}/receipt/invoice/${invoiceId}`,
        GET_BY_ID: (id: string) => `${BASE_URL}/receipt/${id}`,
        CREATE: `${BASE_URL}/receipt`,
        REVERSE: (id: string) => `${BASE_URL}/receipt/${id}/reverse`,
        EDIT: (id: string) => `${BASE_URL}/receipt/${id}/edit`,
        EDIT_HISTORY: (id: string) => `${BASE_URL}/receipt/${id}/edit-history`,
    },
    QUOTATION: {
        BASE: `${BASE_URL}/quotation`,
        LIST_BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/quotation/institution/${institutionId}`,
        LIST_BY_CUSTOMER: (customerId: string) => `${BASE_URL}/quotation/customer/${customerId}`,
        GET_BY_ID: (id: string) => `${BASE_URL}/quotation/${id}`,
        CREATE: `${BASE_URL}/quotation`,
        UPDATE: (id: string) => `${BASE_URL}/quotation/${id}`,
        DELETE: (id: string) => `${BASE_URL}/quotation/${id}`,
        SEND: (id: string) => `${BASE_URL}/quotation/${id}/send`,
        CONFIRM: (id: string) => `${BASE_URL}/quotation/${id}/confirm`,
        REJECT: (id: string) => `${BASE_URL}/quotation/${id}/reject`,
        CONVERT: (id: string) => `${BASE_URL}/quotation/${id}/convert`,
        CLONE: (id: string) => `${BASE_URL}/quotation/${id}/clone`,
        PDF: (id: string) => `${BASE_URL}/quotation/${id}/pdf`,
        EMAIL: (id: string) => `${BASE_URL}/quotation/${id}/email`,
    },
    SALES_REPORT: {
        AGING: (institutionId: string) => `${BASE_URL}/sales-report/aging/institution/${institutionId}`,
        STATEMENT: (customerId: string, from: string, to: string) =>
            `${BASE_URL}/sales-report/statement/customer/${customerId}?from=${from}&to=${to}`,
    },
    SUBSCRIPTION_INVOICE: {
        BASE: `${BASE_URL}/subscription-invoice`,
        BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/subscription-invoice/institution/${institutionId}`,
        ISSUE: (id: string) => `${BASE_URL}/subscription-invoice/${id}/issue`,
        CANCEL: (id: string) => `${BASE_URL}/subscription-invoice/${id}/cancel`,
        MARK_PAID: (id: string) => `${BASE_URL}/subscription-invoice/${id}/mark-paid`,
        PAY_BKASH: (id: string) => `${BASE_URL}/subscription-invoice/${id}/pay/bkash`,
        RESOLVE_PAYMENT: (paymentId: string) => `${BASE_URL}/subscription-invoice/payment/${paymentId}/resolve`,
        MARK_REFUNDED: (paymentId: string) => `${BASE_URL}/subscription-invoice/payment/${paymentId}/refunded`,
    },
    POS_SETTINGS: {
        BY_INSTITUTION: (institutionId: string) => `${BASE_URL}/pos-settings/institution/${institutionId}`,
        SAVE: `${BASE_URL}/pos-settings`,
    },
    POS_TERMINAL: {
        LIST: `${BASE_URL}/pos/terminals`,
        REGISTER: `${BASE_URL}/pos/terminals`,
        RENAME: (id: string) => `${BASE_URL}/pos/terminals/${id}/label`,
        REVOKE: (id: string) => `${BASE_URL}/pos/terminals/${id}/revoke`,
    },
    POS_STORE_ASSIGNMENT: {
        LIST: `${BASE_URL}/pos/store-assignments`,
        ASSIGNABLE_USERS: `${BASE_URL}/pos/store-assignments/assignable-users`,
        CREATE: `${BASE_URL}/pos/store-assignments`,
        DEACTIVATE: (id: string) => `${BASE_URL}/pos/store-assignments/${id}/deactivate`,
        REACTIVATE: (id: string) => `${BASE_URL}/pos/store-assignments/${id}/reactivate`,
    },
    POS_CASHIER_PIN: {
        SET: `${BASE_URL}/pos/cashier-pins`,
    },
    POS_SALE: {
        LIST: `${BASE_URL}/pos/sales`,
        DETAIL: (id: string) => `${BASE_URL}/pos/sales/${id}`,
        VOID: (id: string) => `${BASE_URL}/pos/sales/${id}/void`,
        UNPOSTED: `${BASE_URL}/pos/sales/unposted`,
        POST_NOW: (id: string) => `${BASE_URL}/pos/sales/${id}/post`,
    },
    POS_REPORTS: {
        DAY_END: `${BASE_URL}/pos/reports/day-end`,
        DASHBOARD: `${BASE_URL}/pos/reports/dashboard`,
    },
} as const;
